import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Add16Regular,
    ArrowDownload16Regular,
    Attach16Regular,
    Delete12Regular,
    Dismiss12Regular,
} from '@fluentui/react-icons';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import {
    DeleteTaskAttachment,
    GetTaskAttachments,
    UploadCommentAttachments,
    UploadTaskAttachment,
} from '../../redux/taskSlice';
import { getExtensionIcon, getFileSize, isValidExtensionToUpload } from '../../utils/commonUtils';
import { isPDFTronSupportedAttachment } from '../../utils/filePreviewUtils';
import { downloadTaskAttachmentFile } from '../../utils/taskAttachmentDownloadUtils';
import { validExtensions } from '../../utils/misc';
import styles from './css/index.module.css';

const AttachmentUploadLink = ({ onFiles, label = 'Add an attachment', multiple = true, compact = false }) => {
    const inputRef = useRef(null);

    const handleChange = e => {
        const selected = Array.from(e.target.files || []).filter(file => {
            const ext = file.name.split('.').pop()?.toLowerCase();
            return isValidExtensionToUpload(ext);
        });
        if (selected.length) onFiles?.(selected);
        e.target.value = '';
    };

    return (
        <>
            <button
                type="button"
                className={compact ? styles.attachmentAddLinkCompact : styles.attachmentAddLink}
                onClick={() => inputRef.current?.click()}>
                {compact ? <Attach16Regular /> : <Add16Regular />}
                <span>{label}</span>
            </button>
            <input
                ref={inputRef}
                type="file"
                multiple={multiple}
                accept={validExtensions.map(ext => `.${ext}`).join(',')}
                className={styles.attachmentHiddenInput}
                onChange={handleChange}
            />
        </>
    );
};

export const AttachmentRow = ({
    attachment,
    file,
    currentUserId,
    activeAttachmentId,
    onView,
    onDownload,
    onDelete,
    onRemoveStaged,
    deleting,
    isStaged = false,
}) => {
    const isFile = isStaged && file;
    const extension = isFile
        ? file.name.split('.').pop()?.toLowerCase()
        : attachment?.extension || attachment?.original_filename?.split('.').pop()?.toLowerCase();
    const name = isFile ? file.name : attachment?.original_filename;
    const size = isFile ? file.size : attachment?.file_size;
    const canDelete = !isStaged && currentUserId && attachment?.created_by === currentUserId;
    const canView = !isStaged && isPDFTronSupportedAttachment(attachment);
    const isActive = !isStaged && attachment?.id === activeAttachmentId;

    const handleRowClick = () => {
        if (isStaged) return;
        if (canView) onView?.(attachment);
        else onDownload?.(attachment);
    };

    return (
        <div
            className={`${styles.attachmentRow} ${isActive ? styles.attachmentRowActive : ''}`}
            onClick={handleRowClick}
            role={!isStaged && canView ? 'button' : undefined}
            tabIndex={!isStaged && canView ? -1 : undefined}>
            <div className={styles.attachmentRowIcon}>{getExtensionIcon(extension, 22, 22)}</div>
            <div className={styles.attachmentRowInfo}>
                <span className={styles.attachmentRowName} title={name}>
                    {name}
                </span>
                {size ? <span className={styles.attachmentRowSize}>{getFileSize(size)}</span> : null}
            </div>
            <div className={styles.attachmentRowActions} onClick={e => e.stopPropagation()}>
                {!isStaged && (
                    <button type="button" className={styles.attachmentRowActionBtn} title="Download" onClick={() => onDownload?.(attachment)}>
                        <ArrowDownload16Regular />
                    </button>
                )}
                {isStaged && (
                    <button type="button" className={styles.attachmentRowActionBtn} title="Remove" onClick={() => onRemoveStaged?.(file)}>
                        <Dismiss12Regular />
                    </button>
                )}
                {canDelete && (
                    <DoubleConfirmationModal
                        message="Deleting this attachment will permanently remove it."
                        handleAction={() => onDelete?.(attachment)}>
                        <span className={styles.attachmentRowActionBtn} title="Delete" style={{ opacity: deleting ? 0.5 : 1 }}>
                            <Delete12Regular />
                        </span>
                    </DoubleConfirmationModal>
                )}
            </div>
        </div>
    );
};

const TaskAttachments = ({
    mode = 'task',
    taskId,
    commentId,
    attachments: attachmentsProp,
    stagedFiles,
    onStagedFilesChange,
    showUpload = true,
    label = 'Attachments',
    onView,
    activeAttachmentId,
    compact = false,
    scrollToRequest = null,
    openUploadRequest = null,
    onOpenUploadRequestHandled,
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);
    const { taskAttachments, taskAttachmentLoading } = useSelector(state => state.task);
    const [deletingId, setDeletingId] = React.useState(null);
    const sectionRef = React.useRef(null);

    const attachments = React.useMemo(() => {
        if (mode === 'task') return taskAttachments;
        return attachmentsProp || [];
    }, [mode, taskAttachments, attachmentsProp]);

    React.useEffect(() => {
        if (mode === 'task' && taskId) {
            dispatch(GetTaskAttachments({ taskId }));
        }
    }, [dispatch, mode, taskId]);

    React.useEffect(() => {
        if (scrollToRequest == null || mode !== 'task') return;
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        onOpenUploadRequestHandled?.();
    }, [scrollToRequest, mode, onOpenUploadRequestHandled]);

    React.useEffect(() => {
        if (openUploadRequest == null || mode !== 'task') return;
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        onOpenUploadRequestHandled?.();
    }, [openUploadRequest, mode, onOpenUploadRequestHandled]);

    const handleDownload = async attachment => {
        await downloadTaskAttachmentFile(dispatch, attachment);
    };

    const handleDelete = async attachment => {
        if (!attachment?.id || deletingId) return;
        setDeletingId(attachment.id);
        try {
            await dispatch(DeleteTaskAttachment({ attachmentId: attachment.id }));
        } finally {
            setDeletingId(null);
        }
    };

    const handleUpload = async files => {
        if (!files?.length) return;
        if (mode === 'staged') {
            const merged = [...(stagedFiles || [])];
            files.forEach(f => {
                if (!merged.some(existing => existing.name === f.name && existing.size === f.size)) {
                    merged.push(f);
                }
            });
            onStagedFilesChange?.(merged);
            return;
        }
        if (mode === 'task' && taskId) {
            await dispatch(UploadTaskAttachment({ taskId, files }));
            return;
        }
        if (mode === 'comment' && commentId) {
            await dispatch(UploadCommentAttachments({ commentId, files }));
        }
    };

    const stagedList = mode === 'staged' ? stagedFiles || [] : [];
    const hasRows = attachments?.length > 0 || stagedList.length > 0;
    const isInlineMode = compact || mode === 'staged' || mode === 'comment';

    if (mode === 'staged') {
        if (!hasRows) return null;
        return (
            <div className={styles.commentComposerStaged}>
                <div className={styles.attachmentListFlat}>
                    {stagedList.map((file, index) => (
                        <AttachmentRow
                            key={`staged-${file.name}-${index}`}
                            file={file}
                            isStaged
                            onRemoveStaged={removed => {
                                onStagedFilesChange?.(stagedList.filter(f => f !== removed));
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (isInlineMode) {
        if (!hasRows && !showUpload) return null;
        return (
            <div className={styles.attachmentsSectionCompact}>
                {hasRows && (
                    <div className={styles.attachmentListFlat}>
                        {attachments?.map(item => (
                            <AttachmentRow
                                key={item.id}
                                attachment={item}
                                currentUserId={user?.id}
                                activeAttachmentId={activeAttachmentId}
                                onView={onView}
                                onDownload={handleDownload}
                                onDelete={handleDelete}
                                deleting={deletingId === item.id}
                            />
                        ))}
                    </div>
                )}
                {showUpload && (
                    <div className={styles.commentAttachRow}>
                        <AttachmentUploadLink onFiles={handleUpload} label="Attach file" compact />
                        {taskAttachmentLoading && <span className={styles.attachmentUploading}>Uploading…</span>}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.attachmentsSection} ref={sectionRef}>
            {label ? <p className={styles.attachmentSectionLabel}>{label}</p> : null}

            <div className={styles.attachmentList}>
                {attachments?.map(item => (
                    <AttachmentRow
                        key={item.id}
                        attachment={item}
                        currentUserId={user?.id}
                        activeAttachmentId={activeAttachmentId}
                        onView={onView}
                        onDownload={handleDownload}
                        onDelete={handleDelete}
                        deleting={deletingId === item.id}
                    />
                ))}
                {showUpload && (
                    <div className={styles.attachmentListFooter}>
                        <AttachmentUploadLink onFiles={handleUpload} label="Add an attachment" />
                        {taskAttachmentLoading && <span className={styles.attachmentUploading}>Uploading…</span>}
                    </div>
                )}
            </div>
        </div>
    );
};

export { AttachmentUploadLink };

export default TaskAttachments;
